import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, Eye, Search, Bell, TrendingUp, Play } from 'lucide-react';
import VideoModal from '../components/VideoModal';
import mediaAssets from '../config/mediaAssets';

const Security = ({ onRequestDemo }) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const capabilities = [
    { icon: Bell, title: 'Detect', items: ['Perimeter breach and intrusion alerts', 'Restricted-zone entry', 'Loitering and unusual presence'] },
    { icon: Eye, title: 'Understand', items: ['Person / vehicle classification', 'Movement patterns and flow', 'Occupancy and counting (where needed)'] },
    { icon: TrendingUp, title: 'Act', items: ['Real-time alerts and escalation', 'Acknowledgement and event history', 'Operator-ready dashboards'] },
    { icon: Search, title: 'Investigate', items: ['Filter incidents by time, zone, and event type', 'Review timelines and supporting evidence', 'Build consistent incident records'] }
  ];

  const outcomes = [
    'Faster response and fewer missed events',
    'Reduced false alarms through tuned rules',
    'Faster investigations and clearer evidence trails',
    'Scalable oversight across multiple sites'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-[var(--accent-wash)] to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <Shield className="w-16 h-16 text-[var(--accent-primary)] mx-auto mb-6" />
            <h1 className="heading-1 mb-6">Stop watching screens.<br />Start seeing events.</h1>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto mb-8">
              AI video analytics that helps teams detect, respond, and investigate—with less noise and more clarity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={onRequestDemo} className="btn-primary">Request a Demo</button>
              <Link to="/contact" className="btn-secondary">Talk to an Expert</Link>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl group cursor-pointer"
            onClick={() => setIsVideoOpen(true)}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              poster={mediaAssets.solutionImages.security.hero}
              className="w-full h-[500px] object-cover"
            >
              <source src={mediaAssets.videos.security} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-[var(--accent-primary)] flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all">
                <Play className="w-8 h-8 text-white fill-white ml-1" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What it does */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-2 mb-4">What it does</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {capabilities.map((cap, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="product-card"
              >
                <cap.icon className="w-10 h-10 text-[var(--accent-primary)] mb-4" />
                <h3 className="heading-3 mb-4">{cap.title}</h3>
                <ul className="space-y-2">
                  {cap.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--text-body)]">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Where it fits best */}
      <section className="py-20 px-6 bg-[var(--bg-section)]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-2 mb-4">Where it fits best</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Critical infrastructure and sensitive sites',
              'Warehouses, yards, and industrial facilities',
              'Campuses, commercial properties, public environments',
              'Any multi-site operation that needs consistent standards'
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3 p-6 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-body)] text-lg">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we deliver */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-2 mb-6">How Ferntree delivers it</h2>
            <p className="body-large text-[var(--text-secondary)] mb-8">
              We start with your environment: camera coverage, risk zones, workflows, and response paths.
              Then we deploy, integrate, tune, and train—until alerts are reliable and operators trust what they see.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20 px-6 bg-[var(--bg-section)]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-2 mb-4">Outcomes</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {outcomes.map((outcome, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3 p-6 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-body)] text-lg">{outcome}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-2 mb-6">See what your cameras can do when they're trained to notice.</h2>
            <button onClick={onRequestDemo} className="btn-primary">Request a Demo</button>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        title="Security & Surveillance Demo"
      />
    </div>
  );
};

export default Security;
